import {defineMessages} from 'react-intl'

/**
 * Catálogo de "Mis números" (`/numbers`). Namespace raíz de la Fase 1.10:
 * `telephony.*` — telefonía (números, facturación de Twilio, grupos de
 * asignación, configuración de canales, supervisor).
 *
 * No se traduce:
 * - **Twilio** y demás nombres de proveedor/producto.
 * - **Caller ID / CID**: término de telefonía que el equipo usa en inglés; el ES
 *   lo mantiene tal cual, igual que NPN o Marketplace ID. **Trunk** sí se
 *   traduce: "troncal" (glosario §17.14).
 * - **Números E.164** y los nombres de estado de `helpers/catalogs.ts`
 *   (`US_STATES`): son topónimos de EE. UU., no etiquetas de negocio.
 * - Los `message`/`warnings` que devuelve el backend, que llegan ya redactados.
 */
export const EM_DASH = '—'

export const NUMBERS_MESSAGES = defineMessages({
  // ── Cabecera de la página ──
  title: {id: 'telephony.numbers.title', defaultMessage: 'My Numbers'},
  subtitle: {
    id: 'telephony.numbers.subtitle',
    defaultMessage: 'Personal Caller IDs for inbound and outbound calls.',
  },
  usedOf: {id: 'telephony.numbers.usedOf', defaultMessage: '{used} of {max} used'},

  // ── Autoservicio deshabilitado ──
  disabledTitle: {
    id: 'telephony.numbers.disabled.title',
    defaultMessage: "Number self-service isn't enabled",
  },
  disabledBody: {
    id: 'telephony.numbers.disabled.body',
    defaultMessage:
      'Ask an administrator to enable Twilio number provisioning for your account in Channel Configuration.',
  },

  // ── Resumen de cargos del mes ──
  chargesTitle: {
    id: 'telephony.numbers.charges.title',
    defaultMessage: 'Your charges this month',
  },
  chargesBreakdown: {
    id: 'telephony.numbers.charges.breakdown',
    defaultMessage: 'number rental + voice usage',
  },
  chargesLine: {
    id: 'telephony.numbers.charges.line',
    defaultMessage: '{number}: <b>{amount}</b> ({calls, plural, one {# call} other {# calls}})',
  },

  // ── Tabla "Your numbers" ──
  mineTitle: {id: 'telephony.numbers.mine.title', defaultMessage: 'Your numbers'},
  mineEmpty: {
    id: 'telephony.numbers.mine.empty',
    defaultMessage: "You don't have any numbers yet. Get one below.",
  },
  colNumber: {id: 'common.field.number', defaultMessage: 'Number'},
  colArea: {id: 'telephony.numbers.column.area', defaultMessage: 'Area'},
  colAssignedTo: {id: 'telephony.numbers.column.assignedTo', defaultMessage: 'Assigned to'},
  colRouting: {id: 'telephony.numbers.column.routing', defaultMessage: 'Routing'},
  colOutboundCid: {id: 'telephony.numbers.column.outboundCid', defaultMessage: 'Outbound CID'},
  colActions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  colLocation: {id: 'common.field.location', defaultMessage: 'Location'},
  colAction: {id: 'common.field.action', defaultMessage: 'Action'},

  assignedGroup: {id: 'telephony.numbers.assignedGroup', defaultMessage: 'Group: {name}'},

  trunkReady: {id: 'telephony.numbers.routing.trunkReady', defaultMessage: 'Trunk ✓'},
  trunkPending: {id: 'telephony.numbers.routing.trunkPending', defaultMessage: 'Trunk pending'},
  inboundReady: {id: 'telephony.numbers.routing.inboundReady', defaultMessage: 'Inbound ✓'},
  inboundPending: {
    id: 'telephony.numbers.routing.inboundPending',
    defaultMessage: 'Inbound pending',
  },
  outboundCurrent: {id: 'telephony.numbers.outbound.current', defaultMessage: 'Outbound ✓'},
  outboundUse: {id: 'telephony.numbers.outbound.use', defaultMessage: 'Use for outbound'},

  // SMS: "SMS" es el nombre del canal, no se traduce; el ✓ es símbolo (igual
  // que en trunkReady / inboundReady / outboundCurrent).
  smsEnable: {id: 'telephony.numbers.sms.enable', defaultMessage: 'Enable SMS'},
  smsReady: {id: 'telephony.numbers.sms.ready', defaultMessage: 'SMS ✓'},
  smsIsDefault: {id: 'telephony.numbers.sms.isDefault', defaultMessage: 'SMS default ✓'},
  smsSetDefault: {id: 'telephony.numbers.sms.setDefault', defaultMessage: 'Set default SMS'},

  // ── Acciones ──
  // Assign / Release / Buy y "Select a state…" viven en
  // `helpers/telephonyMessages.ts`: los comparten varias pantallas de la fase.
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  search: {id: 'common.action.search', defaultMessage: 'Search'},
  previous: {id: 'common.action.previous', defaultMessage: 'Previous'},
  next: {id: 'common.action.next', defaultMessage: 'Next'},
  state: {id: 'common.field.state', defaultMessage: 'State'},

  // ── Buscar y comprar un número ──
  searchTitle: {id: 'telephony.numbers.search.title', defaultMessage: 'Get a new number'},
  limitReached: {
    id: 'telephony.numbers.search.limitReached',
    defaultMessage:
      "You've reached your limit of {max, plural, one {# number} other {# numbers}}. Release one to get another, or ask an admin to raise your limit.",
  },
  searching: {id: 'telephony.numbers.search.searching', defaultMessage: 'Searching…'},
  searchEmpty: {
    id: 'telephony.numbers.search.empty',
    defaultMessage: 'No numbers available for that state right now. Try another.',
  },
  showingRange: {
    id: 'common.pagination.showingRange',
    defaultMessage: 'Showing {start}–{end} of {total}',
  },
  pageIndicator: {id: 'common.pagination.pageIndicator', defaultMessage: '{page} / {total}'},

  // ── Toasts y confirmaciones ──
  toastSelectState: {
    id: 'telephony.numbers.toast.selectState',
    defaultMessage: 'Select a state first.',
  },
  toastSearchFailed: {id: 'telephony.numbers.toast.searchFailed', defaultMessage: 'Search failed'},
  toastAcquired: {id: 'telephony.numbers.toast.acquired', defaultMessage: '{number} acquired.'},
  toastPurchaseFailed: {
    id: 'telephony.numbers.toast.purchaseFailed',
    defaultMessage: 'Purchase failed',
  },
  toastOutboundSet: {
    id: 'telephony.numbers.toast.outboundSet',
    defaultMessage: '{number} is now your outbound Caller ID.',
  },
  toastOutboundFailed: {
    id: 'telephony.numbers.toast.outboundFailed',
    defaultMessage: 'Could not set outbound Caller ID',
  },
  toastReleased: {id: 'telephony.numbers.toast.released', defaultMessage: 'Number released.'},
  toastReleaseFailed: {
    id: 'telephony.numbers.toast.releaseFailed',
    defaultMessage: 'Release failed',
  },
  confirmRelease: {
    id: 'telephony.numbers.confirm.release',
    defaultMessage: 'Release {number}? This gives up the number and stops inbound calls to it.',
  },

  // ── Modal de asignación ──
  assignTitle: {id: 'telephony.numbers.assign.title', defaultMessage: 'Assign {number}'},
  assignFallbackNumber: {
    id: 'telephony.numbers.assign.fallbackNumber',
    defaultMessage: 'number',
  },
  assignCurrently: {
    id: 'telephony.numbers.assign.currently',
    defaultMessage: 'Currently: {assignee}',
  },
  assignModeUser: {id: 'telephony.numbers.assign.mode.user', defaultMessage: 'A user'},
  assignModeGroup: {
    id: 'telephony.numbers.assign.mode.group',
    defaultMessage: 'A group (round-robin)',
  },
  assignSelectUser: {id: 'telephony.numbers.assign.selectUser', defaultMessage: 'Select a user…'},
  assignSelectGroup: {
    id: 'telephony.numbers.assign.selectGroup',
    defaultMessage: 'Select a group…',
  },
  assignGroupHint: {
    id: 'telephony.numbers.assign.groupHint',
    defaultMessage: 'The number will ring the group member with the fewest leads today.',
  },
  toastPickDestination: {
    id: 'telephony.numbers.assign.toast.pickDestination',
    defaultMessage: 'Pick a destination first.',
  },
  toastAssigned: {
    id: 'telephony.numbers.assign.toast.assigned',
    defaultMessage: 'Number assigned.',
  },
  toastAssignFailed: {
    id: 'telephony.numbers.assign.toast.failed',
    defaultMessage: 'Could not assign the number',
  },
})
